import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, TrendingUp, TrendingDown, Trophy, AlertCircle, Sparkles, RefreshCw, Info } from "lucide-react";

/**
 * Admin > A/B Test
 *
 * Live conversion stats for the Check-up Light prompt experiment.
 * Two variants are routed 50/50 based on the visitor's session_token hash:
 *   - A: Consulenziale formale ("Lei")
 *   - B: Diretto / Azionabile ("tu", imperativo)
 *
 * The conversion event tracked is: the visitor REGISTERS and the light
 * analysis gets claimed.
 */

const formatPct = (n) => `${(n * 100).toFixed(2)}%`;

const ConfidenceBadge = ({ value, sampleA, sampleB }) => {
  // Show a friendly interpretation of the z-score-derived confidence.
  if (value === null || value === undefined) {
    return (
      <Badge variant="outline" className="border-slate-300 text-slate-600 bg-slate-50">
        <AlertCircle size={12} className="mr-1" /> Campione troppo piccolo
      </Badge>
    );
  }
  const pct = Math.round(value * 100);
  if (pct >= 95) {
    return <Badge className="bg-green-600 hover:bg-green-700 text-white">{pct}% confidenza ✓ statisticamente significativo</Badge>;
  }
  if (pct >= 85) {
    return <Badge className="bg-amber-500 hover:bg-amber-600 text-white">{pct}% confidenza · serve più traffico</Badge>;
  }
  return <Badge variant="outline" className="border-slate-300 text-slate-600 bg-slate-50">{pct}% confidenza · campione piccolo</Badge>;
};

const VariantCard = ({ row, isWinner }) => (
  <Card
    className={`p-6 border-2 transition-all ${
      isWinner ? "border-[#2D6BFF] bg-gradient-to-br from-[#EAF0FF]/40 to-white shadow-md" : "border-slate-200"
    }`}
    data-testid={`ab-variant-card-${row.variant}`}
  >
    <div className="flex items-start justify-between gap-2 mb-3">
      <div>
        <div className="flex items-center gap-2">
          <Badge
            className={isWinner ? "bg-[#2D6BFF] text-white hover:bg-[#1F57F2]" : "bg-slate-200 text-slate-700 hover:bg-slate-300"}
          >
            Variante {row.variant}
          </Badge>
          {isWinner && (
            <Badge className="bg-green-600 text-white hover:bg-green-700">
              <Trophy size={12} className="mr-1" /> Winner
            </Badge>
          )}
        </div>
        <h3 className="mt-1 text-base font-semibold text-slate-900">{row.label}</h3>
      </div>
    </div>

    <div className="grid grid-cols-3 gap-3 mt-4">
      <div>
        <div className="text-xs text-slate-500 uppercase tracking-wide">Analisi</div>
        <div className="text-2xl font-bold text-slate-900 cxo-tabular" data-testid={`ab-total-${row.variant}`}>
          {row.total_analyses}
        </div>
      </div>
      <div>
        <div className="text-xs text-slate-500 uppercase tracking-wide">Registrati</div>
        <div className="text-2xl font-bold text-slate-900 cxo-tabular" data-testid={`ab-claimed-${row.variant}`}>
          {row.claimed}
        </div>
      </div>
      <div>
        <div className="text-xs text-slate-500 uppercase tracking-wide">Conv. rate</div>
        <div
          className={`text-2xl font-bold cxo-tabular ${isWinner ? "text-[#2D6BFF]" : "text-slate-900"}`}
          data-testid={`ab-rate-${row.variant}`}
        >
          {formatPct(row.conversion_rate)}
        </div>
      </div>
    </div>
  </Card>
);

const SAMPLE_PROMPTS = {
  A: {
    label: "Consulenziale formale (Lei)",
    diagnosis: '"La sua azienda sta attraversando una fase critica di crescita non sostenibile..."',
    actions: '"Implementare un sistema di contabilità industriale per centro di costo..."',
  },
  B: {
    label: "Diretto / Azionabile (tu)",
    diagnosis: '"Il problema è che stai crescendo come fatturato ma i conti non tornano..."',
    actions: '"Inizia oggi stesso: mappa i costi diretti su ogni linea di prodotto."',
  },
};

export default function AdminABTests() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [daysWindow, setDaysWindow] = useState("all");

  const load = async (window = daysWindow) => {
    setRefreshing(true);
    try {
      const params = window === "all" ? {} : { days: parseInt(window, 10) };
      const { data } = await api.get("/checkup-light/ab-stats", { params });
      setStats(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load(daysWindow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysWindow]);

  const totalA = stats?.variants?.[0]?.total_analyses ?? 0;
  const totalB = stats?.variants?.[1]?.total_analyses ?? 0;
  const totalSample = totalA + totalB;
  const minSample = stats?.min_sample_size_recommended ?? 100;
  const progressPct = Math.min(100, (totalSample / minSample) * 100);

  const winner = stats?.winner;
  const lift = stats?.lift_percent;

  const sortedRows = useMemo(() => {
    if (!stats?.variants) return [];
    return stats.variants.filter((r) => r.variant === "A" || r.variant === "B");
  }, [stats]);

  const legacyRow = useMemo(() => {
    return stats?.variants?.find((r) => r.variant === "legacy");
  }, [stats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-slate-400" size={28} />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8" data-testid="admin-ab-test-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
            A/B Test · Check-up Light
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Confronto della conversione (visita → registrazione) tra le due varianti di prompt Claude.
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={daysWindow} onValueChange={setDaysWindow}>
            <SelectTrigger className="w-40" data-testid="ab-days-window">
              <SelectValue placeholder="Periodo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tempi</SelectItem>
              <SelectItem value="7">Ultimi 7 giorni</SelectItem>
              <SelectItem value="30">Ultimi 30 giorni</SelectItem>
              <SelectItem value="90">Ultimi 90 giorni</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => load()}
            disabled={refreshing}
            data-testid="ab-refresh-btn"
            className="h-10"
          >
            {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </Button>
        </div>
      </div>

      {/* Summary banner */}
      <Card className="p-5 sm:p-6 border-slate-200 bg-gradient-to-br from-slate-50 to-white">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0">
            <Sparkles size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-slate-900">Verdetto attuale</h3>
            {!winner && (
              <p className="mt-1 text-sm text-slate-600">
                Nessun vincitore ancora — servono più dati di conversione per stabilire una variante migliore.
              </p>
            )}
            {winner && lift !== null && (
              <p className="mt-1 text-sm text-slate-700" data-testid="ab-verdict">
                La variante <strong className="text-[#2D6BFF]">{winner}</strong> converte{" "}
                <strong className="text-green-700">+{lift?.toFixed(1)}%</strong> meglio dell'altra.{" "}
                <ConfidenceBadge value={stats?.confidence} sampleA={totalA} sampleB={totalB} />
              </p>
            )}
            {winner && lift === null && (
              <p className="mt-1 text-sm text-slate-700">
                Una sola variante ha conversioni finora ({winner}). Lascia girare il test ancora un po'.
              </p>
            )}

            {/* Sample-size progress */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
                <span>Campione totale: <strong className="text-slate-900">{totalSample}</strong></span>
                <span>Target consigliato: {minSample}</span>
              </div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${progressPct >= 100 ? "bg-green-600" : "bg-[#2D6BFF]"}`}
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              {progressPct < 100 && (
                <p className="mt-1.5 text-xs text-slate-500">
                  Mancano <strong>{Math.max(0, minSample - totalSample)}</strong> analisi per un read affidabile.
                </p>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Variant cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ab-variant-grid">
        {sortedRows.map((row) => (
          <VariantCard
            key={row.variant}
            row={row}
            isWinner={winner === row.variant && (lift ?? 0) > 0}
          />
        ))}
      </div>

      {/* Sample texts for context */}
      <Card className="p-5 sm:p-6 border-slate-200">
        <h3 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Info size={16} className="text-slate-500" />
          Esempi di output per variante
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {["A", "B"].map((v) => (
            <div key={v} className="rounded-lg border border-slate-200 p-4 bg-slate-50">
              <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-300 mb-2">Variante {v}</Badge>
              <p className="text-xs text-slate-500 mb-2">{SAMPLE_PROMPTS[v].label}</p>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-xs font-semibold text-slate-700">Diagnosi:</span>
                  <p className="text-slate-600 italic mt-0.5">{SAMPLE_PROMPTS[v].diagnosis}</p>
                </div>
                <div>
                  <span className="text-xs font-semibold text-slate-700">Azione:</span>
                  <p className="text-slate-600 italic mt-0.5">{SAMPLE_PROMPTS[v].actions}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Legacy records info */}
      {legacyRow && legacyRow.total_analyses > 0 && (
        <Card className="p-4 sm:p-5 border-slate-200 bg-slate-50">
          <div className="flex items-start gap-3">
            <AlertCircle size={16} className="text-slate-500 shrink-0 mt-0.5" />
            <div className="text-sm text-slate-600">
              Ci sono <strong>{legacyRow.total_analyses}</strong> analisi generate prima dell'avvio dell'A/B test
              (variante non registrata). Conversion rate storico: <strong>{formatPct(legacyRow.conversion_rate)}</strong>.
              Questi record non sono inclusi nel calcolo del winner.
            </div>
          </div>
        </Card>
      )}

      {/* Methodology */}
      <Card className="p-4 sm:p-5 border-slate-200">
        <h4 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
          <Info size={14} className="text-slate-500" />
          Metodologia
        </h4>
        <ul className="space-y-1 text-xs text-slate-600 list-disc pl-5">
          <li>Assegnazione 50/50 deterministica basata su hash del <code className="text-slate-800 bg-slate-100 px-1 rounded">session_token</code> (lo stesso visitatore vede sempre la stessa variante)</li>
          <li>Evento di conversione: registrazione completata con claim dell'analisi light</li>
          <li>Confidenza calcolata via z-test bilaterale (approssimazione normale). Per decisioni critiche si raccomanda &geq;100 record per variante e confidenza &geq;95%</li>
          <li>Modello: <code className="text-slate-800 bg-slate-100 px-1 rounded">claude-sonnet-4-5-20250929</code> via Emergent LLM Key</li>
        </ul>
      </Card>
    </div>
  );
}
